// Import JSON data for dev mode
import heroData from '../data/hero.json';
import booksData from '../data/books.json';
import testimonialData from '../data/testimonial.json';
import pricingData from '../data/pricing.json';
import contactData from '../data/contact.json';
import authorData from '../data/author.json';

// En développement: serveur local Express (port 3001)
// En production: fonctions Netlify
const API_BASE = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:3001/api'
  : '/.netlify/functions';

// DEV_MODE = false => Toujours utiliser l'API (local ou Netlify)
// Plus de localStorage ! Tout est sauvegardé dans Neon
const DEV_MODE = false;

// Demo admin credentials for development (only used when DEV_MODE = true)
const DEV_ADMIN = {
  id: 'dev-admin',
  email: 'admin@udad.com',
  password: 'admin123',
};

/**
 * Get stored auth token
 */
export function getToken() {
  return localStorage.getItem('admin_token');
}

/**
 * Set auth token
 */
export function setToken(token) {
  localStorage.setItem('admin_token', token);
}

/**
 * Remove auth token
 */
export function removeToken() {
  localStorage.removeItem('admin_token');
}

/**
 * Make an authenticated API request
 */
async function apiRequest(endpoint, options = {}) {
  // In development, use mock responses
  if (DEV_MODE) {
    return mockApiResponse(endpoint, options);
  }

  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const url = `${API_BASE}${endpoint}`;
  console.log(`🌐 API Request: ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    console.log(`📥 API Response status: ${response.status}`);
    
    const data = await response.json();
    console.log(`📦 API Response data:`, data);

    if (!response.ok) {
      throw new Error(data.error || 'Une erreur est survenue');
    }

    return data;
  } catch (err) {
    console.error(`❌ API Error for ${url}:`, err);
    throw err;
  }
}

// Local storage keys for dev mode persistence
const DEV_STORAGE_KEY = 'udad_dev_data';

/**
 * Get dev data from localStorage or initialize from JSON
 */
function getDevData() {
  const stored = localStorage.getItem(DEV_STORAGE_KEY);
  if (stored) {
    return JSON.parse(stored);
  }

  // Initialize from JSON files
  const initialData = {
    sections: [
      {
        id: 'hero-ambitieuses',
        type: 'hero',
        name: 'Les ambitieuses',
        slug: 'les-ambitieuses-hero',
        order: 1,
        isActive: true,
        data: heroData.herov2New,
        updatedAt: new Date().toISOString(),
      },
      {
        id: 'book-ambitieuses',
        type: 'book',
        name: 'Les ambitieuses - Détails',
        slug: 'les-ambitieuses-book',
        order: 2,
        isActive: true,
        data: {
          uptitle: booksData.booksNew.uptitle,
          subtitle: booksData.booksNew.subtitle,
          images: [booksData.booksNew.image1, booksData.booksNew.image2, booksData.booksNew.image3, booksData.booksNew.image4],
          themes: booksData.booksNew.booksItem?.booksItemContent || [],
        },
        updatedAt: new Date().toISOString(),
      },
      {
        id: 'hero-dakar',
        type: 'hero',
        name: 'Le dimanche à Dakar',
        slug: 'le-dimanche-a-dakar-hero',
        order: 3,
        isActive: true,
        data: heroData.herov2,
        updatedAt: new Date().toISOString(),
      },
      {
        id: 'book-dakar',
        type: 'book',
        name: 'Le dimanche à Dakar - Détails',
        slug: 'le-dimanche-a-dakar-book',
        order: 4,
        isActive: true,
        data: {
          uptitle: booksData.books.uptitle,
          subtitle: booksData.books.subtitle,
          images: [booksData.books.image1, booksData.books.image2, booksData.books.image3, booksData.books.image4],
          themes: booksData.books.booksItem?.booksItemContent || [],
        },
        updatedAt: new Date().toISOString(),
      },
    ],
    testimonials: (testimonialData.testimonial?.testimonialItem || []).map((t, i) => ({
      id: `testimonial-${t.id || i + 1}`,
      name: t.name,
      title: t.title,
      description: t.description,
      image: t.image,
      bookImage: t.bookImage || '',
      link: t.link || '',
      order: i + 1,
      isActive: true,
    })),
    pricingItems: (pricingData.pricing?.pricingItem || []).map((p, i) => ({
      id: `pricing-${p.id || i + 1}`,
      title: p.title,
      subtitle: p.subtitle || '',
      price: p.price,
      link: p.link || '',
      features: p.pricingList || [],
      isPopular: p.popular || false,
      order: i + 1,
      isActive: true,
    })),
    contactConfig: {
      id: 'main-contact',
      formspreeId: 'mayrvarb',
      defaultSubject: 'Nouveau Message',
      recipientEmail: contactData.contact?.email || '',
      subtitle: contactData.contact?.subtitle || 'CONTACT',
      title: contactData.contact?.title || 'Ecrivez-moi !',
      btnText: contactData.contact?.btnText || 'Envoyer',
    },
    authorConfig: {
      id: 'author-config',
      image: authorData.authorv3?.image || '',
      title: authorData.authorv3?.title || '',
      subtitle: authorData.authorv3?.subtitle || '',
      text: authorData.authorv3?.text || '',
      description: authorData.authorv3?.description || '',
      social: authorData.authorv3?.social || [],
    },
    bookPricingItems: [
      {
        id: 'book-pricing-1',
        bookTitle: 'Le dimanche à Dakar',
        ebookPrice: pricingData.pricing?.pricingItem?.[0]?.price || '5,90€',
        ebookLink: pricingData.pricing?.pricingItem?.[0]?.link || '',
        brochePrice: pricingData.pricing?.pricingItem?.[1]?.price || '16,00€',
        brocheLink: pricingData.pricing?.pricingItem?.[1]?.link || '',
        btnText: 'Acheter',
        order: 1,
        isActive: true,
      },
    ],
  };

  localStorage.setItem(DEV_STORAGE_KEY, JSON.stringify(initialData));
  return initialData;
}

/**
 * Save dev data to localStorage and trigger refresh
 * Always cleans base64 images before saving to prevent quota issues
 */
function saveDevData(data) {
  // Always clean base64 images before saving
  const cleanedData = cleanBase64Images(data);

  try {
    localStorage.setItem(DEV_STORAGE_KEY, JSON.stringify(cleanedData));
  } catch (e) {
    if (e.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, performing aggressive cleanup...');
      // Aggressive cleanup - remove ALL base64 images
      const aggressiveClean = removeAllBase64Images(cleanedData);
      try {
        localStorage.setItem(DEV_STORAGE_KEY, JSON.stringify(aggressiveClean));
      } catch (e2) {
        console.error('Still cannot save. Clearing localStorage completely...');
        localStorage.removeItem(DEV_STORAGE_KEY);
        localStorage.setItem(DEV_STORAGE_KEY, JSON.stringify(aggressiveClean));
      }
    } else {
      throw e;
    }
  }
  // Trigger refresh for components using SiteDataContext
  window.dispatchEvent(new Event('siteDataUpdated'));
}

/**
 * Remove large base64 images from data to save space
 * Keeps URL-based images and small base64 images
 */
function cleanBase64Images(data) {
  // Max 30KB per image - very conservative to prevent quota issues
  const MAX_BASE64_LENGTH = 30000;

  const cleanValue = (value) => {
    if (typeof value === 'string' && value.startsWith('data:image')) {
      if (value.length > MAX_BASE64_LENGTH) {
        console.warn(`Removing large base64 image (${Math.round(value.length / 1024)}KB) to save space`);
        return ''; // Remove large base64 images
      }
    }
    return value;
  };

  const cleanObject = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => cleanObject(item));
    }
    if (obj && typeof obj === 'object') {
      const cleaned = {};
      for (const key of Object.keys(obj)) {
        cleaned[key] = cleanObject(obj[key]);
        // Also check string values that might be base64 images
        if (typeof cleaned[key] === 'string') {
          cleaned[key] = cleanValue(cleaned[key]);
        }
      }
      return cleaned;
    }
    return obj;
  };

  return cleanObject(data);
}

/**
 * Aggressively remove ALL base64 images
 */
function removeAllBase64Images(data) {
  const cleanObject = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => cleanObject(item));
    }
    if (obj && typeof obj === 'object') {
      const cleaned = {};
      for (const key of Object.keys(obj)) {
        cleaned[key] = cleanObject(obj[key]);
        if (typeof cleaned[key] === 'string' && cleaned[key].startsWith('data:image')) {
          console.warn(`Aggressively removing base64 image from key: ${key}`);
          cleaned[key] = '';
        }
      }
      return cleaned;
    }
    return obj;
  };

  return cleanObject(data);
}

/**
 * Mock API responses for development mode
 */
function mockApiResponse(endpoint, options = {}) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const devData = getDevData();

      // Auth login
      if (endpoint === '/auth-login' && options.method === 'POST') {
        const { email, password } = JSON.parse(options.body);
        if (email === DEV_ADMIN.email && password === DEV_ADMIN.password) {
          resolve({
            message: 'Connexion réussie',
            token: 'dev-token-12345',
            admin: { id: DEV_ADMIN.id, email: DEV_ADMIN.email },
          });
        } else {
          reject(new Error('Identifiants invalides'));
        }
        return;
      }

      // Auth verify
      if (endpoint === '/auth-verify') {
        const token = getToken();
        if (token === 'dev-token-12345') {
          resolve({ valid: true, admin: { id: DEV_ADMIN.id, email: DEV_ADMIN.email } });
        } else {
          reject(new Error('Token invalide'));
        }
        return;
      }

      // Sections list
      if (endpoint.startsWith('/sections-list')) {
        resolve({
          sections: devData.sections,
          testimonials: devData.testimonials,
          contactConfig: devData.contactConfig,
          pricingItems: devData.pricingItems,
          authorConfig: devData.authorConfig,
          bookPricingItems: devData.bookPricingItems || [],
        });
        return;
      }

      // Update section/testimonial/pricing
      if (endpoint === '/sections-update' && options.method === 'PUT') {
        const { id, modelType, ...updateData } = JSON.parse(options.body);
        console.log('Updating:', { id, modelType, updateData }); // Debug log

        if (modelType === 'section') {
          const index = devData.sections.findIndex(s => s.id === id);
          if (index !== -1) {
            devData.sections[index] = { ...devData.sections[index], ...updateData, updatedAt: new Date().toISOString() };
            saveDevData(devData);
            resolve({ message: 'Mise à jour réussie', data: devData.sections[index] });
            return;
          }
        } else if (modelType === 'testimonial') {
          const index = devData.testimonials.findIndex(t => t.id === id);
          if (index !== -1) {
            devData.testimonials[index] = { ...devData.testimonials[index], ...updateData };
            saveDevData(devData);
            resolve({ message: 'Mise à jour réussie', data: devData.testimonials[index] });
            return;
          }
        } else if (modelType === 'pricingItem') {
          const index = devData.pricingItems.findIndex(p => p.id === id);
          if (index !== -1) {
            devData.pricingItems[index] = { ...devData.pricingItems[index], ...updateData };
            saveDevData(devData);
            resolve({ message: 'Mise à jour réussie', data: devData.pricingItems[index] });
            return;
          }
        } else if (modelType === 'contactConfig') {
          devData.contactConfig = { ...devData.contactConfig, ...updateData };
          saveDevData(devData);
          resolve({ message: 'Mise à jour réussie', data: devData.contactConfig });
          return;
        } else if (modelType === 'authorConfig') {
          devData.authorConfig = { ...devData.authorConfig, ...updateData };
          saveDevData(devData);
          resolve({ message: 'Mise à jour réussie', data: devData.authorConfig });
          return;
        } else if (modelType === 'bookPricingItem') {
          if (!devData.bookPricingItems) devData.bookPricingItems = [];
          const index = devData.bookPricingItems.findIndex(p => p.id === id);
          if (index !== -1) {
            devData.bookPricingItems[index] = { ...devData.bookPricingItems[index], ...updateData };
            saveDevData(devData);
            resolve({ message: 'Mise à jour réussie', data: devData.bookPricingItems[index] });
            return;
          }
        }
        // Fallback if not found
        reject(new Error(`Élément non trouvé: ${modelType} ${id}`));
        return;
      }

      // Create section/testimonial/pricing
      if (endpoint === '/sections-create' && options.method === 'POST') {
        const { type, ...createData } = JSON.parse(options.body);

        if (type === 'section') {
          const newSection = {
            id: `section-${Date.now()}`,
            ...createData,
            slug: createData.name?.toLowerCase().replace(/\s+/g, '-') || `section-${Date.now()}`,
            order: devData.sections.length + 1,
            isActive: true,
            updatedAt: new Date().toISOString(),
          };
          devData.sections.push(newSection);
          saveDevData(devData);
          resolve({ message: 'Création réussie', data: newSection });
        } else if (type === 'testimonial') {
          const newTestimonial = {
            id: `testimonial-${Date.now()}`,
            ...createData,
            order: devData.testimonials.length + 1,
            isActive: true,
          };
          devData.testimonials.push(newTestimonial);
          saveDevData(devData);
          resolve({ message: 'Création réussie', data: newTestimonial });
        } else if (type === 'pricingItem') {
          const newPricing = {
            id: `pricing-${Date.now()}`,
            ...createData,
            order: devData.pricingItems.length + 1,
            isActive: true,
          };
          devData.pricingItems.push(newPricing);
          saveDevData(devData);
          resolve({ message: 'Création réussie', data: newPricing });
        } else if (type === 'bookPricingItem') {
          if (!devData.bookPricingItems) devData.bookPricingItems = [];
          const newBookPricing = {
            id: `book-pricing-${Date.now()}`,
            ...createData,
            order: devData.bookPricingItems.length + 1,
            isActive: true,
          };
          devData.bookPricingItems.push(newBookPricing);
          saveDevData(devData);
          resolve({ message: 'Création réussie', data: newBookPricing });
        }
        return;
      }

      // Delete section/testimonial/pricing
      if (endpoint === '/sections-delete' && options.method === 'DELETE') {
        const { id, type } = JSON.parse(options.body);

        if (type === 'section') {
          devData.sections = devData.sections.filter(s => s.id !== id);
        } else if (type === 'testimonial') {
          devData.testimonials = devData.testimonials.filter(t => t.id !== id);
        } else if (type === 'pricingItem') {
          devData.pricingItems = devData.pricingItems.filter(p => p.id !== id);
        } else if (type === 'bookPricingItem') {
          if (devData.bookPricingItems) {
            devData.bookPricingItems = devData.bookPricingItems.filter(p => p.id !== id);
          }
        }
        saveDevData(devData);
        resolve({ message: 'Suppression réussie' });
        return;
      }

      // Upload image (just return the same URL in dev mode)
      if (endpoint === '/upload-image' && options.method === 'POST') {
        const { image } = JSON.parse(options.body);
        resolve({ message: 'Upload réussi', url: image });
        return;
      }

      // Default response
      resolve({ message: 'OK (dev mode)' });
    }, 300);
  });
}

// ============ Auth API ============

export async function login(email, password) {
  const data = await apiRequest('/auth-login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  if (data.token) {
    setToken(data.token);
  }

  return data;
}

export async function verifyToken() {
  return apiRequest('/auth-verify');
}

export function logout() {
  removeToken();
}

// ============ Sections API ============

export async function getSections(isAdmin = false) {
  const query = isAdmin ? '?admin=true' : '';
  return apiRequest(`/sections-list${query}`);
}

export async function updateSection(id, modelType, data) {
  return apiRequest('/sections-update', {
    method: 'PUT',
    body: JSON.stringify({ id, modelType, ...data }),
  });
}

export async function createSection(type, data) {
  return apiRequest('/sections-create', {
    method: 'POST',
    body: JSON.stringify({ type, ...data }),
  });
}

export async function deleteSection(id, type) {
  return apiRequest('/sections-delete', {
    method: 'DELETE',
    body: JSON.stringify({ id, type }),
  });
}

// ============ Upload API ============

export async function uploadImage(base64Image, folder = 'udad') {
  return apiRequest('/upload-image', {
    method: 'POST',
    body: JSON.stringify({ image: base64Image, folder }),
  });
}

/**
 * Convert file to base64
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

